import {
  Dictionary,
  DrupalProductNode,
  DrupalSearchApiResponseMeta,
  DrupalSearchApiTerm,
  FacetFilters
} from "@/types/hygiena-types";
import SectionHeading from "../organisms/section-heading";
import {FormattedText} from "../formatted-text";
import {DynamicProductGrid} from "../organisms/widget--product-grid";
import {Pagination} from "../organisms/widget--pagination";
import {createRef, useContext, useEffect, useRef, useState} from "react";
import {DrupalJsonApiParams} from "drupal-jsonapi-params";
import {getProductListingIncludes, productBaseFields, productDocumentationMediaFields} from "../../lib/get-includes";
import {LoadingSpinnerOverlay} from "../atoms/loading-spinner";
import {useRouter} from "next/router";
import {DrupalSearchApiFacet} from "next-drupal";
import {FilterResetButton} from "../atoms/reset-button";
import {DictionaryContext} from "@/context/dictionary-context";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/shadcn/ui/select";
import classNames from "classnames";

/**
 * Gets the parameters for the compatibles query.
 *
 * @param id
 *   The node uuid for the current product page.
 * @param offset
 *   The offset to begin from.
 * @param pageLimit
 *   The page limit.
 * @param locale
 *   The active locale.
 * @param defaultLocale
 *   The default locale.
 * @param filters
 *   Optional facet filters.
 */
function getCompatibleParams(id: string, offset: number, pageLimit: number, locale: string, defaultLocale: string, filters?: FacetFilters): DrupalJsonApiParams {

  const productFields = productBaseFields.concat([
    'id',
    'title',
    'body',
    'metatag',
    'field_product_image',
    'field_product_class',
    'field_product_category',
    'field_product_display_title',
    'field_product_test_targets',
    'field_product_testing_method',
    'field_product_vars',
    'path',
  ]).concat(productDocumentationMediaFields);

  const params = new DrupalJsonApiParams()
    .addGroup('compatibles', 'OR', 'parent')
    .addGroup('parent', 'AND')
    .addFields('node--product', productFields)
    .addFields('taxonomy_term--product_category', [
      'id',
      'name',
      'description',
      'field_testing_method_link',
    ])
    .addInclude(getProductListingIncludes().concat([
      'field_product_category.parent',
      'field_product_class'
    ]))
    .addFilter('status', '1', '=', 'parent')
    .addFilter('langcode', locale ?? defaultLocale, '=', 'parent');

    if (filters) {
      for (const key of Object.keys(filters)) {
        if (filters[key]) {
          params.addFilter(key, filters[key], '=', 'parent');
        }
      }
    }

    // If the product is referenced in any other products Equipment, Automation, Instruments,
    // Color Compensation, Additionals or Monitoring Systems associations, include them as Compatible Products.
    params.addFilter(`field_product_equipment`, id, '=', 'compatibles')
    .addFilter(`field_product_automation`, id, '=', 'compatibles')
    .addFilter(`field_product_instruments`, id, '=', 'compatibles')
    .addFilter(`field_product_additionals`, id, '=', 'compatibles')
    .addFilter(`field_product_real_additionals`, id, '=', 'compatibles')
    .addFilter(`field_product_monitoring_systems`, id, '=', 'compatibles')
    // Also, if the product is part of an extraction, include these product.
    .addFilter(`field_product_ext_automated`, id, '=', 'compatibles')
    .addFilter(`field_product_ext_standard`, id, '=', 'compatibles')
    .addFilter(`field_product_ext_throughput_h`, id, '=', 'compatibles')
    .addFilter(`field_product_ext_high_quality`, id, '=', 'compatibles')

    .addPageLimit(pageLimit)
    .addPageOffset(offset)
    .addSort('title', 'ASC')
    .addSort('brand_name', 'ASC');

  return params;
}

/**
 * Displays a facet filter.
 *
 * @note this uses the original facet filter data from page build, the meta pulled from subsequent searches is
 *       only used to refine the search and grey out unavailable options.
 *
 * @param facet
 *   The facet.
 * @param updateFilter
 *   The update filter callback.
 * @param originalFacet
 *   The original facet from the original search api response data on page load, to confine results.
 * @param activeFacetValue
 *   The active facet value.
 * @param activeFilters
 *   The active filters.
 * @constructor
 */
function FacetFilter({facet, updateFilter, originalFacet, activeFacetValue, activeFilters} : {
  facet: DrupalSearchApiFacet,
  updateFilter: (id: string, value: string|string[]|null) => Promise<void>,
  originalFacet: DrupalSearchApiFacet|undefined,
  activeFacetValue?: string|string[],
  activeFilters: FacetFilters,
}) {
  const filterCount = Object.keys(activeFilters).filter(key => activeFilters?.[key])?.length;

  /**
   * Checks if an option should be disabled or not.
   *
   * @param term
   *   The search api term.
   */
  function isDisabled (term: DrupalSearchApiTerm): boolean|undefined {
    if (filterCount === 1 && activeFilters?.[facet?.path ?? ""]?.length) {
      return false;
    }

    if (facet?.terms?.length) {
      for (const item of facet.terms) {
        if (item.values.value === term.values.value) {
          return false;
        }
      }
    }
    return true;
  }

  return (
    <>
      {originalFacet?.terms?.length ? (
        <Select
            className="border-black border mb-2 lg:mb-0 lg:mx-1 p-1 rounded-sm w-full lg:w-auto block lg:inline md:max-w-[250px]"
            value={activeFacetValue}
            onValueChange={val => updateFilter(facet?.path ?? facet.id, val)}>
          <SelectTrigger className={classNames("border-hygienaGray border rounded-sm text-left font-normal w-auto flex-shrink-0 bg-white")} >
            <SelectValue placeholder={facet.label} />
          </SelectTrigger>
          <SelectContent>
            {originalFacet.terms.map(term => (
              <SelectItem disabled={isDisabled(term)} key={`facet-filter--${facet.id}--${term.values.value}`} value={term.values.value}>{term.values.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ): <></>}
    </>
  )
}

/**
 * Builds out facet filters using Drupal Search API Response meta.
 * @param meta
 *   The original search api response meta.
 * @param activeMeta
 *   The active search api response meta.
 * @param label
 *   The label display next to the filters.
 * @param updateFilter
 *   A callback to update the filter.
 * @param resetFilters
 *   A callback to reset the filters.
 * @param active
 *   True if the filters are active.
 * @param filters
 *   The active filters.
 * @param ignore
 *   Facet paths to ignore, these will not be displayed.
 * @constructor
 */
function FacetFilters({meta, activeMeta, label, updateFilter, resetFilters, active, filters, ignore = []}: {
  meta: DrupalSearchApiResponseMeta|undefined,
  activeMeta: DrupalSearchApiResponseMeta,
  label: string,
  updateFilter: (id: string, value: string|string[]|null) => Promise<void>,
  resetFilters: () => Promise<void>,
  active: boolean,
  filters: FacetFilters,
  ignore: string[],
}) {
  /**
   * Gets the active value for a facet if available.
   *
   * @param facet
   */
  function getActiveValue (facet: DrupalSearchApiFacet|undefined): string|string[] {
    const path = facet?.path ?? "";
    return filters?.[path] ?? ""
  }

  return (
    <div className="flex gap-2 items-center justify-end">
      <div>{label}</div>
      <div className="flex gap-2">
        {activeMeta.facets && activeMeta.facets.filter(facet => !ignore.includes(facet?.path ?? "")).map(facet => (
          <FacetFilter key={`facet-filter--${facet.id}`}
                       originalFacet={meta?.facets?.filter(item => item?.id === facet?.id)?.[0]}
                       facet={facet}
                       activeFilters={filters}
                       activeFacetValue={getActiveValue(facet)}
                       updateFilter={updateFilter}/>
        ))}
        <div className="flex items-center justify-center">
          <FilterResetButton callback={resetFilters} enabled={active}/>
        </div>
      </div>
    </div>
  )
}

/**
 * Compatible products are grouped by a header and description, and the products are broken out into an
 * accordion by product category.
 *
 * @note this uses Search API to fetch compatible products and to provide filtering.
 *
 * @param id
 *   The parent product id these compatibles apply to.
 * @param products
 *   The products.
 * @param gridKey
 *   A unique grid key.
 * @param title
 *   The section heading title.
 * @param description
 *   The description to display beneath the section header title.
 * @param meta
 *   Search API Response meta, containing item count and facets.
 */
export function CompatiblesProductGrid({id, products, gridKey, title, description, meta}: {
  id: string,
  products: DrupalProductNode[],
  gridKey: string,
  title: {processed: string},
  description: {processed: string},
  meta?: DrupalSearchApiResponseMeta,
}): JSX.Element {
  const router = useRouter();
  const t = useContext<Dictionary>(DictionaryContext);
  const [activeProducts, setActiveProducts] = useState<DrupalProductNode[]>(products);
  const [activeMeta, setActiveMeta] = useState<DrupalSearchApiResponseMeta|undefined>(meta);
  const [page, setPage] = useState<number>(0);
  const [pageLimit, setPageLimit] = useState<number>(6);
  const [loading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<FacetFilters>({});
  const activeFilters = Object.keys(filters).filter(key => filters[key]?.length)?.length > 0;
  const ref = createRef<HTMLDivElement>();
  const activeCategory = getActiveCategory();

  async function searchCompatibles(pageLimit: number, page: number, filters: FacetFilters) {
    let productData: DrupalProductNode[];
    let productMeta: DrupalSearchApiResponseMeta;

    const params = getCompatibleParams(id, page * pageLimit, pageLimit, router.locale ?? "en", router.defaultLocale ?? "en", filters);
    try {
      const response = await fetch(`/api/compatible-products?${params.getQueryString()}`, {
        method: 'POST',
        body: JSON.stringify({
          query: params.getQueryString(),
          locale: router.locale,
          defaultLocale: router.defaultLocale,
        }),
      });
      const responseJson = await response.json();

      productData = responseJson?.products ?? [];
      productMeta = responseJson?.meta ?? {};
    }
    catch (error) {
      productData = products;
      productMeta = meta ?? {};
    }

    if (productMeta?.facets) {
      productMeta.facets = productMeta?.facets.sort((a, b) => {
        if (a?.path === "field_product_category_parent" || b?.label === "field_product_category_parent") return -1;
        return a?.label?.localeCompare(b?.label ?? "") ?? 0;
      });
    }
    setActiveMeta(productMeta);
    setActiveProducts(productData ?? []);
    setLoading(false);
  }

  /**
   * Handler for pagination events.
   */
  async function changePage(page) {
    setLoading(true);
    setPage(page);
    if (ref?.current) ref?.current.scrollIntoView({behavior: 'smooth'})
    await searchCompatibles(pageLimit, page, filters);
  }

  /**
   * Updates a filter value and re-fetches the compatibles.
   *
   * @param id
   *   The facet path.
   * @param value
   *   The filter value.
   */
  async function updateFilter(id: string, value: string|string[]|null): Promise<void> {
    setLoading(true);
    setPage(0);
    const newFilters = filters;
    newFilters[id] = value;
    setFilters(newFilters);
    await searchCompatibles(pageLimit, 0, newFilters);
  }

  /**
   * Resets the filters.
   */
  async function resetFilters(): Promise<void> {
    setLoading(true);
    setFilters({});
    setPage(0);
    await searchCompatibles(pageLimit, 0, {});
  }

  /**
   * Gets the active product category label, if available.
   */
  function getActiveCategory(): string|undefined {
    // @todo Localize.
    if (filters?.field_product_category_parent && meta?.facets?.length) {
      const categoryFacet = meta.facets.filter(facet => facet.path = "field_product_category_parent")?.[0];
      if (categoryFacet?.terms?.length) {
        const categoryValue = categoryFacet.terms.filter(term => term.values.value === filters.field_product_category_parent)?.[0];
        return categoryValue?.values?.label ?? t.all.all_products;
      }
    }
    return t.all.all_products;
  }

  return (
    <>
      <div className="container mx-auto" key={`compatibles--container--${gridKey}`} ref={ref}>
        <SectionHeading title={undefined} displayTitle={title?.processed}>
          {description?.processed && <FormattedText processed={description?.processed}/>}
        </SectionHeading>
        {/* Filters are taken from the Search API Result meta. */}
        <div className="relative">
          {products?.length > 1 ? (
            <>
              {activeMeta && (
                // @todo localize this.
                <FacetFilters activeMeta={activeMeta} meta={meta} label={`${t.all.filter_products}:`}
                              ignore={["field_product_testing_method"]}
                              updateFilter={updateFilter} resetFilters={resetFilters} active={activeFilters} filters={filters}/>
              )}
            </>
          ) : <></>}
          {activeCategory && (
            <h3>{activeCategory}</h3>
          )}
          <DynamicProductGrid nodes={activeProducts} filters={false}/>
          <LoadingSpinnerOverlay loading={loading} fixed={true}/>
        </div>
        <Pagination count={activeMeta?.count ?? 0} page={page} limit={pageLimit} changePage={changePage} radius={5}/>
      </div>
    </>
  )
}

